import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChartOptions } from 'chart.js'
import { Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import {
  DateRange,
  RuuviTagCondition,
  RuuviTagConditionAggregate,
  RuuviTagConditionAggregateQuery,
  RuuviTagConditionAggregateStatistic,
  RuuviTagConditionQuery,
  RuuviTagConditionStatistic
} from '../types'
import RuuviTagConditionChart from './RuuviTagConditionChart'
import RuuviTagConditionAggregateChart from './RuuviTagConditionAggregateChart'
import RuuviTagConditionStatisticToggle from './RuuviTagConditionStatisticToggle'
import LoadingState from './LoadingState'
import { getDifferenceOfDateRangeInHours } from '../utils/date'

interface RuuviTagHumidityChartProps {
  conditions: RuuviTagCondition[] | RuuviTagConditionAggregate[]
  query: RuuviTagConditionQuery | RuuviTagConditionAggregateQuery

  /**
   * The date range used for charts.
   */
  dateRange?: DateRange

  loading?: boolean
}

export default function RuuviTagHumidityChart({
  conditions,
  query,
  dateRange,
  loading = false
}: RuuviTagHumidityChartProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The condition statistics of RuuviTag.
   */
  const [statistics, setStatistics] = useState<
    RuuviTagConditionStatistic[] | RuuviTagConditionAggregateStatistic[]
  >(['avg'])

  /**
   * Determines whether aggregates are being used.
   */
  const isUsingAggregates =
    getDifferenceOfDateRangeInHours(
      dateRange ? dateRange.from : query.from,
      dateRange ? dateRange.to : query.to
    ) >= 24

  /**
   * The chart options.
   */
  const chartOptions: ChartOptions<'line'> = {
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        suggestedMax: 100,
        suggestedMin: 0,
        grid: {
          display: true
        }
      }
    }
  }

  /**
   * The highest humidity.
   */
  const highestHumidity = Math.max(
    ...conditions.map((ruuviTagCondition) => ruuviTagCondition.humidityMax)
  )

  /**
   * The lowest humidity.
   */
  const lowestHumidity = Math.min(
    ...conditions.map((ruuviTagCondition) => ruuviTagCondition.humidityMin)
  )

  /**
   * The average humidity.
   */
  const averageHumidity =
    conditions
      .map((ruuviTagCondition) => ruuviTagCondition.humidityAvg)
      .reduce((a, b) => a + b, 0) / conditions.length

  /**
   * Make the chip label.
   */
  function makeChipLabel(label: string, value: number): string {
    return `${label}: ${value.toFixed(2)}%`
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4} display="flex" justifyContent="flex-start" alignItems="center">
            <Typography variant="h4">{t('humidity')}</Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            justifyContent={{ xs: 'flex-start', md: 'center' }}
            alignItems="center"
          >
            {conditions.length > 0 && (
              <Stack direction="row" gap={3}>
                <Chip
                  label={makeChipLabel(t('min'), lowestHumidity)}
                  size="small"
                  variant="outlined"
                />

                <Chip
                  label={makeChipLabel(t('avg'), averageHumidity)}
                  size="small"
                  variant="outlined"
                />

                <Chip
                  label={makeChipLabel(t('max'), highestHumidity)}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            display="flex"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            alignItems="center"
          >
            <RuuviTagConditionStatisticToggle value={statistics} onChange={setStatistics} />
          </Grid>
        </Grid>

        {loading ? (
          <LoadingState />
        ) : (
          <>
            {isUsingAggregates ? (
              <RuuviTagConditionAggregateChart
                field="humidity"
                conditions={conditions as RuuviTagConditionAggregate[]}
                query={query as RuuviTagConditionAggregateQuery}
                dateRange={dateRange}
                statistics={statistics as RuuviTagConditionStatistic[]}
                options={chartOptions}
              />
            ) : (
              <RuuviTagConditionChart
                ruuviTagConditionField="humidity"
                ruuviTagConditions={conditions as RuuviTagCondition[]}
                ruuviTagConditionQuery={query as RuuviTagConditionQuery}
                ruuviTagConditionStatistics={statistics as RuuviTagConditionStatistic[]}
                dateRange={dateRange}
                chartOptions={chartOptions}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
